import type { EmbeddingMessage, EmbeddingMessageType } from '../../../types/embedding';

export const SHELL_MESSAGE_SOURCE = 'portal-shell';
export const EMBEDDED_APP_MESSAGE_SOURCE = 'portal-embedded';
const EMBEDDING_MESSAGE_TYPES = new Set<EmbeddingMessageType>([
  'auth',
  'auth:expired',
  'consent',
  'theme',
  'language',
  'navigation',
  'custom',
  'ready',
  'resize',
  'error',
]);

/**
 * Creates a formatted message to send to an embedded service
 */
export function createMessage<T>(
  type: EmbeddingMessageType,
  payload: T
): EmbeddingMessage<T> {
  return {
    type,
    payload,
    source: SHELL_MESSAGE_SOURCE,
    timestamp: Date.now(),
  };
}

/**
 * Validates if a message is a valid embedding message
 */
export function isValidMessage(event: MessageEvent): boolean {
  // Ignore messages from self
  if (event.source === window) {
    return false;
  }

  // Basic structure check
  const data = event.data;
  return isEmbeddingMessage(data);
}

/**
 * Type guard for embedding messages
 */
export function isEmbeddingMessage(data: unknown): data is EmbeddingMessage {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const message = data as Record<string, unknown>;
  return (
    typeof message.type === 'string'
    && EMBEDDING_MESSAGE_TYPES.has(message.type as EmbeddingMessageType)
    && typeof message.source === 'string'
    && 'payload' in message
    && typeof message.timestamp === 'number'
    && Number.isFinite(message.timestamp)
  );
}

/**
 * Validates the origin of a message against allowed origins
 */
export function isValidOrigin(origin: string, allowedOrigins: string[]): boolean {
  if (allowedOrigins.includes('*')) {
    return true;
  }
  return allowedOrigins.includes(origin);
}
